import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Divider,
  InputAdornment,
  FormGroup,
} from "@mui/material";
import {
  FaUser,
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaEuroSign,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaEdit,
  FaClock,
  FaCalendar,
} from "react-icons/fa";
import {
  updateProfileData,
  resetProfile,
  addSkill,
  removeSkill,
  addCertification,
  removeCertification,
  updateAvailability,
  updateRates,
  setLoading,
  setError,
} from "../redux/slices/userProfileSlice";
import {
  saveMyProfile,
  fetchMyProfile,
} from "../redux/slices/userProfileSlice";
import ProfilePhotoUploader from "../components/ProfilePhotoUploader";
import SkillSelector from "../components/SkillSelector";
import CertificationList from "../components/CertificationList";
import "../styles/EditProfile.css";

const EditProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, skills, certifications, loading, error } = useSelector(
    (state) => state.userProfile,
  );
  // Le slice userProfile utilise parfois un objet de loading; dériver un booléen local
  const isLoading = typeof loading === "boolean" ? loading : !!loading?.profile;

  // États locaux pour le formulaire
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    bio: "",
    city: "",
    country: "",
    website: "",
    hourlyRate: "",
    dailyRate: "",
    currency: "EUR",
    experience: "",
    languages: [],
    specialties: [],
    identityCard: null,
    criminalRecord: null,
  });

  const [availability, setAvailabilityState] = useState({
    weekdays: false,
    weekends: false,
    evenings: false,
    mornings: false,
    afternoons: false,
  });

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialiser le formulaire avec les données du profil
  useEffect(() => {
    if (profile) {
      const adaptedForm = {
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        phone: profile.phone || "",
        bio: profile.bio || "",
        city: profile.address?.city || profile.city || "",
        country: profile.address?.country || profile.country || "",
        website: profile.socialLinks?.website || profile.website || "",
        hourlyRate:
          (profile.professional?.hourlyRate ?? profile.hourlyRate) || "",
        dailyRate: (profile.professional?.dailyRate ?? profile.dailyRate) || "",
        currency: (profile.professional?.currency ?? profile.currency) || "EUR",
        experience: profile.experience || "",
        languages: profile.languages || [],
        specialties:
          (profile.professional?.specialties ?? profile.specialties) || [],
      };
      setFormData(adaptedForm);

      const profAvail = profile.professional?.availability;
      const profPrefs = profile.professional?.availabilityPreferences || {};
      const existingAvail = profile.availability || {};
      const adaptedAvailability = {
        weekdays: profAvail
          ? ["monday", "tuesday", "wednesday", "thursday", "friday"].some(
              (d) => profAvail[d]?.available,
            )
          : !!(profPrefs.weekdays ?? existingAvail.weekdays),
        weekends: profAvail
          ? ["saturday", "sunday"].some((d) => profAvail[d]?.available)
          : !!(profPrefs.weekends ?? existingAvail.weekends),
        evenings: !!(profPrefs.evenings ?? existingAvail.evenings),
        mornings: !!(profPrefs.mornings ?? existingAvail.mornings),
        afternoons: !!(profPrefs.afternoons ?? existingAvail.afternoons),
      };
      setAvailabilityState(adaptedAvailability);
    }
  }, [profile]);

  useEffect(() => {
    dispatch(fetchMyProfile());
  }, [dispatch]);

  // Gérer les changements dans le formulaire
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);

    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAvailabilityChange = (field, checked) => {
    setAvailabilityState((prev) => ({ ...prev, [field]: checked }));
    setHasChanges(true);
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: "Le fichier est trop volumineux (max 5 Mo)",
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, [fieldName]: file }));
      setValidationErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "Le prénom est obligatoire";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Le nom est obligatoire";
    }

    if (!formData.email.trim()) {
      errors.email = "L'email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Le téléphone est obligatoire";
    }

    if (!formData.bio.trim()) {
      errors.bio = "La biographie est obligatoire";
    } else if (formData.bio.length < 50) {
      errors.bio = "La biographie doit contenir au moins 50 caractères";
    }

    if (!formData.city.trim()) {
      errors.city = "La ville est obligatoire";
    }

    if (!formData.country.trim()) {
      errors.country = "Le pays est obligatoire";
    }

    if (
      formData.hourlyRate &&
      (isNaN(formData.hourlyRate) || formData.hourlyRate < 0)
    ) {
      errors.hourlyRate = "Le tarif horaire doit être un nombre positif";
    }

    if (
      formData.dailyRate &&
      (isNaN(formData.dailyRate) || formData.dailyRate < 0)
    ) {
      errors.dailyRate = "Le tarif journalier doit être un nombre positif";
    }

    // Vérification des documents obligatoires pour les professionnels
    if (profile?.role === "professional") {
      if (
        !formData.identityCard &&
        !profile?.professional?.legalDocuments?.identityCard?.url
      ) {
        errors.identityCard = "La carte d'identité est obligatoire";
      }
      if (
        !formData.criminalRecord &&
        !profile?.professional?.legalDocuments?.criminalRecord?.url
      ) {
        errors.criminalRecord =
          "Le bulletin n°3 du casier judiciaire est obligatoire";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    if (!validateForm()) {
      setShowErrorMessage(true);
      return;
    }

    try {
      // Préparation des données pour l'envoi
      // Si on envoie des fichiers, il faut utiliser FormData
      // Pour l'instant, on suppose que saveMyProfile gère ça ou on adapte ici si besoin
      // Note: Le slice actuel semble gérer un objet JSON, il faudra adapter le slice
      // ou transformer en FormData si l'API attend du multipart/form-data pour les fichiers

      const submitData = new FormData();

      // Ajout des champs texte
      Object.keys(formData).forEach((key) => {
        if (
          key !== "identityCard" &&
          key !== "criminalRecord" &&
          key !== "languages" &&
          key !== "specialties"
        ) {
          submitData.append(key, formData[key]);
        }
      });

      // Tableaux
      submitData.append("languages", JSON.stringify(formData.languages));
      submitData.append("specialties", JSON.stringify(formData.specialties));
      submitData.append("availability", JSON.stringify(availability));

      // Documents (si nouveaux fichiers)
      if (formData.identityCard instanceof File) {
        submitData.append("identityCard", formData.identityCard);
      }
      if (formData.criminalRecord instanceof File) {
        submitData.append("criminalRecord", formData.criminalRecord);
      }

      await dispatch(saveMyProfile({ formData: submitData })).unwrap();
      setShowSuccessMessage(true);
      setHasChanges(false);
    } catch (err) {
      // Afficher le message d'erreur remonté par l'API si disponible
      const apiMessage = typeof err === "string" ? err : err?.message;
      dispatch(
        setError(apiMessage || "Erreur lors de la sauvegarde du profil"),
      );
      setShowErrorMessage(true);
    }
  };

  // Annuler les modifications
  const handleCancel = () => {
    if (hasChanges) {
      const confirmCancel = window.confirm(
        "Vous avez des modifications non sauvegardées. Voulez-vous vraiment annuler ?",
      );
      if (!confirmCancel) return;
    }

    dispatch(resetProfile());
    navigate("/profile");
  };

  // Retour vers le profil
  const handleBackToProfile = () => {
    if (hasChanges) {
      const confirmLeave = window.confirm(
        "Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?",
      );
      if (!confirmLeave) return;
    }

    navigate("/profile");
  };

  const availabilityOptions = [
    { key: "weekdays", label: "Semaine (Lun-Ven)", icon: <FaCalendar /> },
    { key: "weekends", label: "Week-ends", icon: <FaCalendar /> },
    { key: "mornings", label: "Matinées", icon: <FaClock /> },
    { key: "afternoons", label: "Après-midis", icon: <FaClock /> },
    { key: "evenings", label: "Soirées", icon: <FaClock /> },
  ];

  const currencyOptions = [
    { value: "EUR", label: "€ Euro" },
    { value: "USD", label: "$ Dollar" },
    { value: "GBP", label: "£ Livre" },
    { value: "CHF", label: "CHF Franc Suisse" },
  ];

  return (
    <>
      <Helmet>
        <title>Modifier mon profil - Mission Sport</title>
        <meta
          name="description"
          content="Modifiez vos informations personnelles, compétences, tarifs et disponibilités sur Mission Sport. Optimisez votre profil pour attirer plus d'opportunités."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box className="edit-profile-page">
        <Container maxWidth="lg" className="edit-profile-container">
          {/* En-tête avec fil d'Ariane */}
          <Box className="edit-profile-header">
            <Breadcrumbs className="breadcrumbs">
              <Link href="/" color="inherit">
                Accueil
              </Link>
              <Link href="/profile" color="inherit">
                Mon profil
              </Link>
              <Typography color="primary">Modifier mon profil</Typography>
            </Breadcrumbs>

            <Box className="header-actions">
              <Button
                startIcon={<FaArrowLeft />}
                onClick={handleBackToProfile}
                variant="outlined"
                className="back-button"
              >
                Retour au profil
              </Button>
            </Box>
          </Box>

          <Typography variant="h3" className="page-title">
            <FaEdit className="title-icon" />
            Modifier mon profil
          </Typography>

          <Typography variant="body1" className="page-subtitle">
            Mettez à jour vos informations pour optimiser votre visibilité sur
            Mission Sport
          </Typography>

          {/* Formulaire principal */}
          <Paper className="edit-form-container" elevation={0}>
            <Grid container spacing={4}>
              {/* Colonne gauche - Informations personnelles */}
              <Grid item xs={12} lg={6}>
                <Box className="form-section">
                  <Typography variant="h5" className="section-title">
                    <FaUser className="section-icon" />
                    Informations personnelles
                  </Typography>

                  {/* Photo de profil */}
                  <Box className="photo-section">
                    <ProfilePhotoUploader
                      currentPhoto={profile?.avatar}
                      onPhotoChange={(photo) =>
                        handleInputChange("avatar", photo)
                      }
                    />
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Prénom"
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange("firstName", e.target.value)
                        }
                        error={!!validationErrors.firstName}
                        helperText={validationErrors.firstName}
                        required
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nom"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange("lastName", e.target.value)
                        }
                        error={!!validationErrors.lastName}
                        helperText={validationErrors.lastName}
                        required
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaEnvelope />
                            </InputAdornment>
                          ),
                        }}
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Téléphone"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        error={!!validationErrors.phone}
                        helperText={validationErrors.phone}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaPhone />
                            </InputAdornment>
                          ),
                        }}
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Ville"
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange("city", e.target.value)
                        }
                        error={!!validationErrors.city}
                        helperText={validationErrors.city}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaMapMarkerAlt />
                            </InputAdornment>
                          ),
                        }}
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Pays"
                        value={formData.country}
                        onChange={(e) =>
                          handleInputChange("country", e.target.value)
                        }
                        error={!!validationErrors.country}
                        helperText={validationErrors.country}
                        required
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Biographie / Présentation"
                        multiline
                        rows={4}
                        value={formData.bio}
                        onChange={(e) =>
                          handleInputChange("bio", e.target.value)
                        }
                        error={!!validationErrors.bio}
                        helperText={
                          validationErrors.bio ||
                          `${formData.bio.length}/500 caractères`
                        }
                        required
                        inputProps={{ maxLength: 500 }}
                        className="form-field"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Colonne droite - Compétences et tarifs */}
              <Grid item xs={12} lg={6}>
                {/* Compétences */}
                <Box className="form-section">
                  <SkillSelector
                    skills={skills}
                    onAddSkill={(skill) => dispatch(addSkill(skill))}
                    onRemoveSkill={(skillId) => dispatch(removeSkill(skillId))}
                  />
                </Box>

                <Divider className="section-divider" />

                {/* Certifications */}
                <Box className="form-section">
                  <CertificationList
                    certifications={certifications}
                    onAddCertification={(cert) =>
                      dispatch(addCertification(cert))
                    }
                    onRemoveCertification={(certId) =>
                      dispatch(removeCertification(certId))
                    }
                  />
                </Box>

                <Divider className="section-divider" />

                {/* Tarifs */}
                <Box className="form-section">
                  <Typography variant="h6" className="section-title">
                    <FaEuroSign className="section-icon" />
                    Tarifs
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Tarif horaire"
                        type="number"
                        value={formData.hourlyRate}
                        onChange={(e) =>
                          handleInputChange("hourlyRate", e.target.value)
                        }
                        error={!!validationErrors.hourlyRate}
                        helperText={validationErrors.hourlyRate}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {formData.currency === "EUR"
                                ? "€"
                                : formData.currency}
                            </InputAdornment>
                          ),
                        }}
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Tarif journalier"
                        type="number"
                        value={formData.dailyRate}
                        onChange={(e) =>
                          handleInputChange("dailyRate", e.target.value)
                        }
                        error={!!validationErrors.dailyRate}
                        helperText={validationErrors.dailyRate}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {formData.currency === "EUR"
                                ? "€"
                                : formData.currency}
                            </InputAdornment>
                          ),
                        }}
                        className="form-field"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth className="form-field">
                        <InputLabel>Devise</InputLabel>
                        <Select
                          value={formData.currency}
                          onChange={(e) =>
                            handleInputChange("currency", e.target.value)
                          }
                          label="Devise"
                        >
                          {currencyOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                <Divider className="section-divider" />

                {/* Documents légaux */}
                <Box className="form-section">
                  <Typography variant="h6" className="section-title">
                    <FaUser className="section-icon" />
                    Documents légaux (Obligatoires)
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Carte d'identité *
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                        }}
                      >
                        {formData.identityCard instanceof File
                          ? formData.identityCard.name
                          : profile?.professional?.legalDocuments?.identityCard
                                ?.url
                            ? "Document déjà téléchargé (cliquer pour remplacer)"
                            : "Choisir un fichier..."}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, "identityCard")}
                        />
                      </Button>
                      {validationErrors.identityCard && (
                        <Typography color="error" variant="caption">
                          {validationErrors.identityCard}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Bulletin n°3 du casier judiciaire (moins de 3 mois) *
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                        }}
                      >
                        {formData.criminalRecord instanceof File
                          ? formData.criminalRecord.name
                          : profile?.professional?.legalDocuments
                                ?.criminalRecord?.url
                            ? "Document déjà téléchargé (cliquer pour remplacer)"
                            : "Choisir un fichier..."}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) =>
                            handleFileChange(e, "criminalRecord")
                          }
                        />
                      </Button>
                      {validationErrors.criminalRecord && (
                        <Typography color="error" variant="caption">
                          {validationErrors.criminalRecord}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Divider className="section-divider" />

                {/* Disponibilités */}
                <Box className="form-section">
                  <Typography variant="h6" className="section-title">
                    <FaClock className="section-icon" />
                    Disponibilités
                  </Typography>

                  <FormGroup className="availability-group">
                    {availabilityOptions.map((option) => (
                      <FormControlLabel
                        key={option.key}
                        control={
                          <Checkbox
                            checked={availability[option.key]}
                            onChange={(e) =>
                              handleAvailabilityChange(
                                option.key,
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box className="availability-label">
                            {option.icon}
                            <span>{option.label}</span>
                          </Box>
                        }
                        className="availability-item"
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Grid>
            </Grid>

            {/* Boutons d'action */}
            <Box className="action-buttons">
              <Button
                variant="outlined"
                size="large"
                startIcon={<FaTimes />}
                onClick={handleCancel}
                disabled={isLoading}
                className="cancel-button"
              >
                Annuler
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={<FaSave />}
                onClick={handleSave}
                disabled={isLoading || !hasChanges}
                className="save-button"
              >
                {isLoading ? "Sauvegarde..." : "Enregistrer les modifications"}
              </Button>
            </Box>
          </Paper>
        </Container>

        {/* Messages de feedback */}
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={4000}
          onClose={() => setShowSuccessMessage(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowSuccessMessage(false)}
            severity="success"
            variant="filled"
          >
            Profil mis à jour avec succès !
          </Alert>
        </Snackbar>

        <Snackbar
          open={showErrorMessage}
          autoHideDuration={6000}
          onClose={() => setShowErrorMessage(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowErrorMessage(false)}
            severity="error"
            variant="filled"
          >
            {error || "Veuillez corriger les erreurs dans le formulaire"}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default EditProfile;
